/**
 * Frankencoin savings-module read surface.
 *
 * The module is **not a token** and **not ERC-4626**: there are no
 * shares, no `asset()`, no `totalAssets()` and no `convertToAssets()`.
 * A position is an internal account (`savings(address) → {saved, ticks,
 * referrer, referralFeePPM}`) whose `saved` grows in place, so the
 * module's own ZCHF balance *is* the book — read with the shared
 * `BalanceOfAbi` against ZCHF, not against the module.
 *
 * Only `currentRatePPM` is called here. It is the module's identity
 * proof: `balanceOf` alone succeeds against *any* address, so a
 * mis-pinned module would silently publish a plausible-looking row —
 * see [../readers/frankencoinSavings.ts](../readers/frankencoinSavings.ts).
 *
 * The rest of the surface is deliberately absent because nothing in the
 * public-data leg calls it: `accruedInterest(address)` /
 * `refreshBalance(address)` / `savings(address)` are per-user reads
 * (phase 2), and `save` / `withdraw` / `adjust` are writes that live in
 * the calldata-sdk. `INTEREST_DELAY` is a constant 3 days — documented
 * in the registry entry rather than spent as a multicall slot.
 *
 * NB the live module (`0x27d9AD98…`, the `savingsReferral` deployment)
 * carries the referral overloads and a 4-field `Account`; the
 * `@frankencoin/zchf` package's `savingsV2` export (`0x3BF301B0…`) is
 * the retired 1 % module with the 2-field one. `currentRatePPM` is
 * identical on both, so this fragment is version-agnostic.
 */
export const FrankencoinSavingsReadAbi = [
  {
    /** Savings rate in parts per million — `35000` = 3.5 % nominal APR.
     *  `0` means the module is disabled (`save` reverts with
     *  `ModuleDisabled`); existing balances stay withdrawable. */
    name: 'currentRatePPM',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint24' }],
  },
] as const
